<!-- dashboard/routes/dashboard/settings/account/components/auto-resolve/DefaultConfigForm.vue -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import multiselect from 'vue-multiselect';

const { t } = useI18n();

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  labelOptions: {
    type: Array,
    default: () => [],
  },
  durationOptions: {
    type: Array,
    default: () => [],
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'update:config']);

const duration = computed({
  get: () => props.config.duration,
  set: (val) => updateConfig('duration', val),
});

const unit = computed({
  get: () => props.config.unit,
  set: (val) => updateConfig('unit', val),
});

const selectedUnit = computed({
  get: () => props.durationOptions.find((o) => o.value === unit.value) || null,
  set: (opt) => {
    unit.value = opt?.value ?? 'minutes';
  },
});

const message = computed({
  get: () => props.config.message,
  set: (val) => updateConfig('message', val),
});

const labelToApply = computed({
  get: () => props.config.labelToApply,
  set: (val) => updateConfig('labelToApply', val),
});

const ignoreWaiting = computed({
  get: () => props.config.ignoreWaiting,
  set: (val) => updateConfig('ignoreWaiting', val),
});

const updateConfig = (key, value) => {
  emit('update:config', { ...props.config, [key]: value });
};

const handleSubmit = () => {
  emit('submit');
};
</script>

<template>
  <div class="space-y-4">
    <div>
      <h3 class="text-sm font-medium text-n-slate-12">
        Configuração Padrão
      </h3>
      <p class="text-sm text-n-slate-10 mt-0.5">
        Aplicada quando não há configuração específica para a conversa.
      </p>
    </div>

    <form class="space-y-4" @submit.prevent="handleSubmit">
      <div>
        <label class="block text-sm text-n-slate-11 mb-1.5">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL') }}
        </label>
        <div class="grid grid-cols-[1fr_140px] gap-2">
          <input
            v-model.number="duration"
            type="number"
            min="1"
            max="1000000"
            class="w-full h-9 px-3 text-sm bg-n-alpha-black2 border-0 outline outline-1 outline-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand-ink transition-colors"
            placeholder="10"
          />
          <multiselect
            v-model="selectedUnit"
            class="multiselect-sm !mb-0 w-full text-sm"
            :options="durationOptions"
            track-by="value"
            label="label"
            :allow-empty="false"
            :show-labels="false"
            :searchable="false"
            placeholder="Unidade"
          />
        </div>
      </div>

      <div>
        <label class="block text-sm text-n-slate-11 mb-1.5">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL') }}
          <span class="text-n-slate-9">(opcional)</span>
        </label>
        <TextArea
          v-model="message"
          class="w-full"
          rows="2"
          :placeholder="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')"
        />
      </div>

      <div class="space-y-3 p-4 bg-n-solid-2 rounded-xl border border-n-weak">
        <div class="flex items-center justify-between">
          <label class="text-sm text-n-slate-11">
            {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL') }}
          </label>
          <Switch v-model="ignoreWaiting" />
        </div>
        
        <div class="flex items-center justify-between">
          <label class="text-sm text-n-slate-11">
            {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
          </label>
          <SingleSelect
            v-model="labelToApply"
            :options="labelOptions"
            :placeholder="$t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')"
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </div>
      </div>

      <div class="flex justify-end pt-2">
        <NextButton
          blue
          sm
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>